import { ObjectKeys } from 'src/util';
import { SA, OnChange } from 'src/util/Redux';

export type IO<I = {}, O = {}> = { input: I, output: O };

export type CacheEntry<T extends IO> = (
  { input: T['input'] } & (
    { type: 'wanted' } |
    { type: 'working' } |
    { type: 'failed', error: Error } |
    { type: 'resolved', output: T['output'] }
  )
);

function PreferredEntry<T extends IO>(
  a: CacheEntry<T>,
  b: CacheEntry<T>,
): CacheEntry<T> {
  const typeList: CacheEntry<T>['type'][] = [
    'wanted',
    'working',
    'failed',
    'resolved'
  ];

  return typeList.indexOf(a.type) > typeList.indexOf(b.type) ? a : b;
}

export type Type<T extends IO> = {
  [inputStr in string]?: CacheEntry<T>;
};

export function Type<T extends IO>(): Type<T> {
  return {};
}

export type Action<T extends IO> = (
  { type: 'set', data: Type<T> } |
  { type: 'resolve', data: Type<T> }
);

export function reduce<T extends IO>(
  cache: Type<T>,
  action: Action<T>,
): Type<T> {
  switch (action.type) {
    case 'set': {
      const newCache = Type<T>();

      for (const inputStr of ObjectKeys(action.data)) {
        const setEntry = action.data[inputStr];

        if (!setEntry) {
          continue;
        }

        const existingEntry = cache[inputStr];

        if (!existingEntry) {
          newCache[inputStr] = setEntry;
        } else {
          newCache[inputStr] = PreferredEntry(existingEntry, setEntry);
        }
      }

      return newCache;
    }

    case 'resolve': {
      const newCache = { ...cache };

      for (const inputStr of ObjectKeys(action.data)) {
        const existingEntry = newCache[inputStr];
        const resolveEntry = action.data[inputStr];

        if (existingEntry && resolveEntry) {
          newCache[inputStr] = PreferredEntry(existingEntry, resolveEntry);
        }
      }

      return newCache;
    }
  }
}

export function get<T extends IO>(
  cache: Type<T>,
  input: T['input'],
): T['output'] | null {
  const entry = cache[JSON.stringify(input)];

  if (!entry || entry.type !== 'resolved') {
    return null;
  }

  return entry.output;
}

export function OnChange<
  T extends IO,
  X extends SA
>(
  getWanted: (state: X['state']) => IO['input'][],
  calculator: (input: T['input']) => Promise<T['output']>,
  getCache: (state: X['state']) => Type<T>,
  mapAction: (action: Action<T>) => X['action'],
): OnChange<X> {
  return (state, dispatch) => {
    function dispatchCache(action: Action<T>) {
      dispatch(mapAction(action));
    }

    const wanted = getWanted(state);
    const existingCache: Type<T> = getCache(state);
    const newCache: Type<T> = {};

    for (const input of wanted) {
      const inputStr = JSON.stringify(input);
      const existingEntry = existingCache[inputStr];

      let newEntry = existingEntry || { type: 'wanted', input };

      if (newEntry.type === 'wanted') {
        calculator(input).then(
          output => dispatchCache({
            type: 'resolve',
            data: {
              [inputStr]: {
                type: 'resolved',
                input,
                output,
              },
            },
          }),
          error => dispatchCache({
            type: 'resolve',
            data: {
              [inputStr]: {
                type: 'failed',
                input,
                error,
              },
            },
          }),
        );

        newEntry = { type: 'working', input };
      }

      newCache[inputStr] = newEntry;
    }

    if (JSON.stringify(existingCache) !== JSON.stringify(newCache)) {
      dispatchCache({
        type: 'set',
        data: newCache,
      });
    }
  };
}



// WEBPACK FOOTER //
// ./src/state/CalculationCache.ts